import React from 'react';
import { AlertCircle, CheckCircle, Info, X, XCircle } from 'lucide-react';

interface AlertProps {
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  onDismiss?: () => void;
  dismissible?: boolean;
  className?: string;
}

const Alert: React.FC<AlertProps> = ({
  type,
  title,
  message,
  onDismiss,
  dismissible = false,
  className = ''
}) => {
  const baseClasses = "rounded-md p-4 border";
  
  const typeStyles = {
    success: {
      container: "bg-green-50 border-green-200",
      icon: CheckCircle,
      iconColor: "text-green-400",
      title: "text-green-800",
      message: "text-green-700"
    },
    error: {
      container: "bg-red-50 border-red-200", 
      icon: XCircle,
      iconColor: "text-red-400",
      title: "text-red-800",
      message: "text-red-700"
    },
    warning: {
      container: "bg-yellow-50 border-yellow-200",
      icon: AlertCircle,
      iconColor: "text-yellow-400", 
      title: "text-yellow-800",
      message: "text-yellow-700"
    },
    info: {
      container: "bg-blue-50 border-blue-200",
      icon: Info,
      iconColor: "text-blue-400",
      title: "text-blue-800", 
      message: "text-blue-700"
    }
  };

  const style = typeStyles[type];
  const Icon = style.icon;

  return (
    <div className={`${baseClasses} ${style.container} ${className}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <Icon className={`h-5 w-5 ${style.iconColor}`} />
        </div>
        <div className="ml-3 flex-1">
          {title && (
            <h3 className={`text-sm font-medium ${style.title}`}>
              {title}
            </h3>
          )}
          <div className={`text-sm ${title ? 'mt-1' : ''} ${style.message}`}>
            {message}
          </div>
        </div>
        {dismissible && onDismiss && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                onClick={onDismiss}
                className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 hover:bg-opacity-20 ${
                  type === 'success' ? 'text-green-500 hover:bg-green-500 focus:ring-green-600' :
                  type === 'error' ? 'text-red-500 hover:bg-red-500 focus:ring-red-600' :
                  type === 'warning' ? 'text-yellow-500 hover:bg-yellow-500 focus:ring-yellow-600' :
                  'text-blue-500 hover:bg-blue-500 focus:ring-blue-600'
                }`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Alert;